import { SearchProvider, useSearch } from "@/context/SearchContext";
import PropertyContent from "@/components/property/PropertyContent";
import PropertyFilters from "@/components/property/PropertyFilters";
import PropertyHeader from "@/components/property/PropertyHeader";
import PropertySearchBar from "@/components/property/PropertySearchBar";
import { useListingFilters } from "@/hooks/useListingFilters";
import { useTitle } from "@/hooks/useTitle";
import { useState, useEffect, useCallback } from "react"; // Added useCallback
import { useSearchParams } from "react-router-dom"; // Added useSearchParams
import { useQueryClient } from "@tanstack/react-query"; // Added useQueryClient
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// New sub-component to consume SearchContext
const PropertyListingsLayout = () => {
  const { filters, updateFilters, clearFilters } = useListingFilters();
  const {
    searchQuery,
    setSearchQuery,
    handleSearch,
    invalidateDuplicateQueries,
  } = useSearch();
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams(); // Added useSearchParams
  const queryClient = useQueryClient(); // Get queryClient

  const activeCategoryName = "All Properties";

  const handlePropertySearchBarSubmit = useCallback(
    (params: {
      // Wrapped with useCallback
      searchTerm?: string;
      locationSlug?: string;
    }) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", "1"); // Always reset page on new search

      if (params.locationSlug) {
        // If a location slug is provided, use the 'location' parameter
        newParams.set("location", params.locationSlug);
        newParams.delete("search_term"); // Clear search term if a specific location is selected
      } else if (params.searchTerm) {
        // If a general search term is provided, use 'search_term'
        newParams.set("search_term", params.searchTerm);
        newParams.delete("location"); // Clear location if searching by general term
      } else {
        // If neither is provided (e.g., clear search), clear both
        newParams.delete("search_term");
        newParams.delete("location");
      }

      invalidateDuplicateQueries(); // Invalidate queries
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, invalidateDuplicateQueries]
  );

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow container mx-auto px-4 py-8">
        <PropertyHeader
          activeCategoryName={activeCategoryName}
          setShowFilterSheet={setShowFilterSheet}
        />
        <div className="my-6">
          <PropertySearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-full lg:w-1/4">
            <PropertyFilters
              initialFilters={filters}
              onFilterChange={updateFilters}
            />
          </aside>

          {/* Mobile Filter Sheet */}
          <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
            <SheetContent
              side="left"
              className="w-[300px] sm:w-[400px] p-0 overflow-y-auto"
            >
              <SheetHeader className="p-4 border-b sticky top-0 bg-background z-10">
                <div className="flex justify-between items-center">
                  <SheetTitle>Filters</SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon">
                      <X className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="p-4">
                <PropertyFilters
                  initialFilters={filters}
                  onFilterChange={updateFilters}
                />
              </div>
            </SheetContent>
          </Sheet>

          {/* Main Content */}
          <div className="w-full lg:w-3/4">
            <PropertyContent filters={filters} onClearFilters={clearFilters} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default function PropertyListings() {
  useTitle("Property Listings - CommercialProperty.lk");

  return (
    <SearchProvider>
      <PropertyListingsLayout />
    </SearchProvider>
  );
}
